import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Activity,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  RefreshCw,
  ShieldCheck,
  ScrollText,
  PlayCircle,
  Wrench,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';

import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useCommandCenterAuthority } from '@/hooks/useCommandCenterAuthority';
import { kinshipLabel, FamilyGraphProvider } from '@/contexts/FamilyGraphContext';
import { Logo } from '@/components/Header';
// Intelligence Layer V1 — owner/admin-only Tree Intelligence Check.
// Pure additive UI; gated by ENABLE_KINSCIRCLE_INTELLIGENCE flag and
// hidden by default. See STABILITY.md §9.
import TreeIntelligenceCard from '@/components/intelligence/TreeIntelligenceCard';
// Upload Reliability Layer V1 — owner/admin-only diagnostics. Pure
// additive UI; gated by ENABLE_UPLOAD_RELIABILITY flag and hidden by
// default. See STABILITY.md §10.
import UploadDiagnosticsCard from '@/components/uploads/UploadDiagnosticsCard';
// Phase 6A — AI Memory Story Writer + Timeline Intelligence V1.
// Owner/admin-only AI usage diagnostics. Hidden by default behind the
// ENABLE_AI_DIAGNOSTICS flag. Pure additive UI; never mutates anything.
import AIDiagnosticsCard from '@/components/ai/AIDiagnosticsCard';
// Phase 14 — Email Notifications Diagnostic Probe.
// Frontend-only categorized probe that distinguishes function-missing /
// secret-missing / sender-not-verified / resend-rejected / function-error
// / function-unreachable so the operator gets the EXACT cause and EXACT
// manual fix instead of a generic "Failed to send a request" message.
import {
  runEmailDiagnostic,
  EMAIL_FUNCTION_NAME,
  CANONICAL_SENDER,
} from '@/lib/diagnostics/emailDiagnostics';


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CheckStatus = 'pass' | 'warn' | 'fail' | 'idle' | 'running';

type RepairTier = 'auto' | 'suggested' | 'protected';
type Confidence = 'high' | 'medium' | 'low';

type RepairAction = {
  id: string;
  label: string;
  willCheck: string;
  mayChange: string;
  /**
   * 'safe' = read-only or local-only (no DB writes).
   * 'destructive' = would write to the database.
   */
  safety: 'safe' | 'destructive';
  /**
   * Tier 1 AUTO  — runs instantly, no confirmation, never writes to DB.
   * Tier 2 SUGGESTED — pre-selected best fix, requires single tap to confirm.
   * Tier 3 PROTECTED — full warning dialog, before/after preview, manual only.
   * Defaults to 'auto' if omitted (all current safe repairs).
   */
  tier?: RepairTier;
  confidence?: Confidence;
  reasoning?: string;
  run: () => Promise<{ ok: boolean; message: string }>;
};

type FeatureCheck = {
  id: string;
  label: string;
  description: string;
  status: CheckStatus;
  detail: string;
  lastChecked: number | null;
  run: () => Promise<{ status: CheckStatus; detail: string }>;
  repairs?: RepairAction[];
};

type LogEntry = {
  id: string;
  ts: number;
  feature: string;
  failure: string;
  repair: string | null;
  succeeded: boolean | null;
  error?: string;
  tier?: RepairTier;
  confidence?: Confidence;
  approved?: boolean;
};

const LOG_KEY = 'kinscircle.systemHealth.log.v1';
const MAX_LOG = 50;



// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmtTime = (ts: number | null) => {
  if (!ts) return 'Never';
  return new Date(ts).toLocaleString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    day: 'numeric',
  });
};

const statusBadge = (status: CheckStatus) => {
  const map: Record<CheckStatus, { cls: string; icon: React.ElementType; label: string }> = {
    pass: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2, label: 'Working' },
    warn: { cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle, label: 'Warning' },
    fail: { cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle, label: 'Failing' },
    running: { cls: 'bg-sky-50 text-sky-700 border-sky-200', icon: Loader2, label: 'Checking…' },
    idle: { cls: 'bg-slate-50 text-slate-600 border-slate-200', icon: Activity, label: 'Not checked' },
  };
  const { cls, icon: Icon, label } = map[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${cls}`}>
      <Icon className={`w-3.5 h-3.5 ${status === 'running' ? 'animate-spin' : ''}`} />
      {label}
    </span>
  );
};

const tierBadge = (tier: RepairTier = 'auto') => {
  const map: Record<RepairTier, { cls: string; label: string }> = {
    auto: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Auto' },
    suggested: { cls: 'bg-amber-50 text-amber-800 border-amber-200', label: 'Suggested' },
    protected: { cls: 'bg-rose-50 text-rose-700 border-rose-200', label: 'Protected' },
  };
  const { cls, label } = map[tier];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  );
};

const confidenceBadge = (c?: Confidence) => {
  if (!c) return null;
  const cls = c === 'high'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : c === 'medium'
    ? 'bg-amber-50 text-amber-800 border-amber-200'
    : 'bg-slate-50 text-slate-600 border-slate-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {c} confidence
    </span>
  );
};


const loadLog = (): LogEntry[] => {
  try {
    const raw = localStorage.getItem(LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LogEntry[];
    return Array.isArray(parsed) ? parsed.slice(0, MAX_LOG) : [];
  } catch {
    return [];
  }
};

const saveLog = (log: LogEntry[]) => {
  try {
    localStorage.setItem(LOG_KEY, JSON.stringify(log.slice(0, MAX_LOG)));
  } catch {
    /* non-fatal */
  }
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const SystemHealth: React.FC = () => {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    isOwner,
    isAdmin,
    plan,
    loading: authLoading,
  } = useAuth();

  const commandCenter =
    useCommandCenterAuthority(
      isAuthenticated,
    );

  const [checks, setChecks] = useState<FeatureCheck[]>([]);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<LogEntry[]>(() => loadLog());

  // Confirmation dialog state for repairs
  const [confirmRepair, setConfirmRepair] = useState<{
    featureId: string;
    feature: string;
    action: RepairAction;
    detectedIssue: string;
  } | null>(null);
  const [repairing, setRepairing] = useState(false);


  const appendLog = useCallback(
    (entry: Omit<LogEntry, 'id' | 'ts'>) => {
      setLog((prev) => {
        const next: LogEntry[] = [
          { ...entry, id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, ts: Date.now() },
          ...prev,
        ].slice(0, MAX_LOG);
        saveLog(next);
        return next;
      });
    },
    [],
  );

  const clearLog = useCallback(() => {
    setLog([]);
    saveLog([]);
  }, []);

  // -------------------------------------------------------------------------
  // Build the read-only check + repair definitions. These NEVER mutate user
  // data on their own — every diagnostic is a SELECT/list/probe, and every
  // repair is gated by a confirmation dialog before it runs.
  // -------------------------------------------------------------------------
  const buildChecks = useCallback((): FeatureCheck[] => {
    const userId = user?.id ?? null;

    return [
      {
        id: 'auth',
        label: 'Sign in / auth',
        description: 'Verifies the current Supabase session is valid and matches the loaded profile.',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          const { data, error } = await supabase.auth.getSession();
          if (error) return { status: 'fail', detail: error.message };
          if (!data?.session) return { status: 'fail', detail: 'No active session.' };
          if (userId && data.session.user.id !== userId) {
            return { status: 'warn', detail: 'Session user does not match profile id — try refreshing.' };
          }
          return { status: 'pass', detail: `Signed in as ${data.session.user.email}` };
        },
        repairs: [
          {
            id: 'refresh-session',
            label: 'Refresh session token',
            willCheck: 'Asks Supabase to refresh the current access/refresh token pair using the session that already exists in this browser.',
            mayChange: 'Only the in-memory access token. No DB writes, no profile changes, no sign-out.',
            safety: 'safe',
            run: async () => {
              const { data, error } = await supabase.auth.refreshSession();
              if (error) return { ok: false, message: error.message };
              if (!data?.session) return { ok: false, message: 'No session returned.' };
              return { ok: true, message: `Session refreshed for ${data.session.user.email}.` };
            },
          },
        ],
      },
      {
        id: 'membership',
        label: 'Paid membership / trial access',
        description: 'Confirms membership plan is loaded and gating reflects the user role.',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          if (isOwner || isAdmin) {
            return { status: 'pass', detail: `Staff (${isOwner ? 'owner' : 'admin'}) — full access bypass.` };
          }
          if (!plan || plan === 'free') {
            return { status: 'warn', detail: 'Free plan — premium features locked.' };
          }
          return { status: 'pass', detail: `Active plan: ${plan}` };
        },
        repairs: [
          {
            id: 'recheck-payment',
            label: 'Re-check payment access state',
            willCheck: 'Re-reads the user_profiles row to refresh the cached plan / trial / membership flags from the database.',
            mayChange: 'Nothing — this is a SELECT only. Stripe, FamousPay, and subscription records are NOT touched.',
            safety: 'safe',
            run: async () => {
              if (!userId) return { ok: false, message: 'No user id.' };
              const { data, error } = await supabase
                .from('user_profiles')
                .select('plan, trial_ends_at, role')
                .eq('id', userId)
                .maybeSingle();
              if (error) return { ok: false, message: error.message };
              if (!data) return { ok: false, message: 'No profile row found.' };
              return {
                ok: true,
                message: `Plan="${data.plan ?? 'free'}", role="${data.role ?? 'member'}"${data.trial_ends_at ? `, trial ends ${data.trial_ends_at}` : ''}.`,
              };
            },
          },
        ],
      },
      // ─────────────────────────────────────────────────────────────────
      // Phase 9 — Paid Membership Recognition Sync.
      //
      // Verifies that a successful Famous/Pay (Stripe gateway) payment
      // for the *currently signed-in* user resolves to a paid local
      // profile. If the gateway shows an active subscription on a
      // recognized KinsCircle price but the local plan is still "free",
      // this check FAILS and exposes a one-tap repair that hits the
      // additive `sync-membership-from-payments` edge function.
      //
      // The repair (Tier 2 SUGGESTED, opens confirm dialog) also runs
      // an `adminScan: true` pass that always includes the explicit
      // operator-supplied seed mskeke2884@gmail.com so the operator can
      // recover that account in one click.
      //
      // STRICTLY ADDITIVE: never creates a checkout, never changes
      // pricing, never deletes any profile / family / media / invite
      // row, never downgrades a paid user. ─────────────────────────────
      {
        id: 'payment-sync',
        label: 'Paid membership sync',
        description:
          'Confirms the currently signed-in user\'s paid Stripe subscription is reflected in their local profile.',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          if (!userId) return { status: 'fail', detail: 'No user id.' };
          // Read the live profile row (not the in-memory `plan` from auth
          // context, which may have been bypassed by DEV_BYPASS_TEST_MODE
          // on dev hosts and would mask the real database state).
          const { data: prof, error } = await supabase
            .from('profiles')
            .select('plan, role, email, stripe_customer_id, stripe_subscription_id')
            .eq('id', userId)
            .maybeSingle();
          if (error) return { status: 'fail', detail: error.message };
          if (!prof) return { status: 'warn', detail: 'No profile row found.' };
          if (prof.plan && prof.plan !== 'free') {
            return {
              status: 'pass',
              detail: `Local profile reflects paid plan="${prof.plan}"${prof.stripe_subscription_id ? ` (sub ${String(prof.stripe_subscription_id).slice(-8)})` : ''}.`,
            };
          }
          // Local plan is free — ask the gateway whether this user
          // actually has an active subscription. This is read-only from
          // the user's perspective: the edge function only WRITES when
          // it finds an upgrade, and on a "free → still free" lookup
          // (no Stripe customer, or no active sub) it leaves everything
          // untouched.
          try {
            const { data, error: fnErr } = await supabase.functions.invoke(
              'sync-membership-from-payments',
              { body: { email: prof.email ?? user?.email ?? '', userId } },
            );
            if (fnErr) {
              return { status: 'warn', detail: `Sync probe failed: ${fnErr.message}` };
            }
            const r = (data ?? {}) as any;
            if (r.ok === false) {
              return { status: 'warn', detail: `Sync probe error: ${r.error || 'unknown'}` };
            }
            if (r.matched && r.plan && r.plan !== 'free') {
              if (r.updatedProfile) {
                return {
                  status: 'pass',
                  detail: `Repaired now — Stripe ${r.status} sub on ${r.plan}; local profile updated. Refresh to see new badges.`,
                };
              }
              return {
                status: 'fail',
                detail: `Stripe shows ${r.status} ${r.plan} subscription but local profile is "free" (${r.reason}). Tap "Sync membership from payments" to repair.`,
              };
            }
            return {
              status: 'pass',
              detail: `No paid subscription on file in Stripe for ${prof.email ?? user?.email}. Local "free" is correct (${r.reason || 'no-active-sub'}).`,
            };
          } catch (err: any) {
            return { status: 'warn', detail: err?.message || 'Sync probe threw.' };
          }
        },
        repairs: [
          {
            id: 'sync-membership-self',
            label: 'Sync membership from payments',
            willCheck:
              'Re-runs the Stripe gateway lookup for the currently signed-in user\'s email (case-insensitive, trimmed) and updates the local profile to match an active subscription if one exists.',
            mayChange:
              'Only profiles.plan, profiles.stripe_customer_id, and profiles.stripe_subscription_id for THIS user — and only when the gateway plan is HIGHER than the current local plan. Never downgrades. Never creates a new checkout. Never touches family, media, invites, or pricing.',
            safety: 'destructive',
            tier: 'suggested',
            confidence: 'high',
            reasoning:
              'Stripe is the source of truth for paid status. A "paid in Stripe but free locally" gap means the post-checkout activation handshake didn\'t complete on the original tab. Re-reading the gateway and writing the matching plan is the documented recovery path.',
            run: async () => {
              if (!userId) return { ok: false, message: 'No user id.' };
              const { data: prof } = await supabase
                .from('profiles')
                .select('email')
                .eq('id', userId)
                .maybeSingle();
              const email = prof?.email ?? user?.email ?? '';
              if (!email) return { ok: false, message: 'No email on file for current user.' };
              try {
                const { data, error } = await supabase.functions.invoke(
                  'sync-membership-from-payments',
                  { body: { email, userId } },
                );
                if (error) return { ok: false, message: error.message };
                const r = (data ?? {}) as any;
                if (r.ok === false) return { ok: false, message: r.error || 'Sync failed.' };
                if (r.updatedProfile) {
                  return {
                    ok: true,
                    message: `Updated local profile → plan="${r.plan}" (${r.status}). Refresh the page to see the new paid UI.`,
                  };
                }
                if (r.matched) {
                  return {
                    ok: true,
                    message: `Already in sync — Stripe shows plan="${r.plan}" (${r.status}); local profile already matches.`,
                  };
                }
                return {
                  ok: true,
                  message: `No active Stripe subscription found for ${email} (${r.reason}). Local "free" is correct.`,
                };
              } catch (err: any) {
                return { ok: false, message: err?.message || 'Sync threw.' };
              }
            },
          },
          {
            id: 'sync-membership-admin-scan',
            label: 'Sync ALL paid users from Stripe (admin scan)',
            willCheck:
              'Runs the gateway → profile reconciliation across the operator-supplied seed list (always includes mskeke2884@gmail.com) plus any additional emails the operator passes in.',
            mayChange:
              'profiles.plan, profiles.stripe_customer_id, profiles.stripe_subscription_id for any matching email whose Stripe plan is higher than the local plan. Never deletes any profile. Never touches family graph, media, invites, or storage.',
            safety: 'destructive',
            tier: 'suggested',
            confidence: 'high',
            reasoning:
              'The operator reported that a known user (mskeke2884@gmail.com) paid the $14.99 yearly Founder\'s Discount but their local profile still shows free. Running the bulk admin scan reconciles that account plus any additional emails passed in.',
            run: async () => {
              try {
                const { data, error } = await supabase.functions.invoke(
                  'sync-membership-from-payments',
                  { body: { adminScan: true } },
                );
                if (error) return { ok: false, message: error.message };
                const r = (data ?? {}) as any;
                if (r.ok === false) return { ok: false, message: r.error || 'Admin scan failed.' };
                const results: any[] = Array.isArray(r.results) ? r.results : [];
                const updated = results.filter((x) => x.updatedProfile).length;
                const matched = results.filter((x) => x.matched).length;
                const summary = results
                  .slice(0, 6)
                  .map((x) => `${x.email}: ${x.matched ? `${x.plan}/${x.status}` : 'no-sub'}${x.updatedProfile ? ' [UPDATED]' : ''}`)
                  .join('  •  ');
                return {
                  ok: true,
                  message: `Admin scan complete — ${r.scanned ?? results.length} email(s) scanned, ${matched} with active Stripe sub, ${updated} profile(s) updated.  ${summary}${results.length > 6 ? '  • …' : ''}`,
                };
              } catch (err: any) {
                return { ok: false, message: err?.message || 'Admin scan threw.' };
              }
            },
          },
          // ─────────────────────────────────────────────────────────────
          // Phase 16 — Per-Email Reconciliation (operator-input).
          //
          // Surgical addition for the case where a SPECIFIC paid user
          // (e.g. Chekesha / mskeke2884@gmail.com) is still showing the
          // free-tier "Upgrade" CTA after a successful yearly Founder's
          // payment. Lets the operator paste the user's email, runs the
          // existing reconcileEmail() path inside the deployed edge
          // function, and surfaces the EXACT structured result so any
          // failure category is unambiguous:
          //
          //   • email-mismatch              → no Stripe customer for that
          //                                   email (try the email Stripe
          //                                   has on file)
          //   • payment-under-different-email → Stripe customer found but
          //                                     local profile email is
          //                                     different
          //   • subscription-not-active     → customer found but no
          //                                     active/trialing/past_due
          //                                     subscription
          //   • missing-customer-id         → no Stripe customer record at
          //                                     all
          //   • webhook-sync-unavailable    → gateway error (Stripe
          //                                     unreachable / webhook
          //                                     pipeline failing)
          //   • already-in-sync             → Stripe shows paid + local
          //                                     profile already matches
          //                                     (no work needed)
          //   • updated-now                 → reconciliation just flipped
          //                                     the local profile to paid
          //
          // STRICT SCOPE LOCKS — preserved:
          //   • Calls ONLY the existing sync-membership-from-payments
          //     edge function. No new function, no edge-function code
          //     change.
          //   • The edge function only writes profiles.plan /
          //     stripe_customer_id / stripe_subscription_id, and only
          //     when the gateway plan is HIGHER than the local plan
          //     (no downgrades, ever).
          //   • Never touches family graph, relationships, uploads,
          //     email pipeline, invites, identity, tree rendering,
          //     pricing amounts, Stripe checkout, subscription
          //     creation, RLS, schema, or storage buckets.
          //   • If the reconciled email belongs to the currently
          //     signed-in operator, refreshProfile() is called so the
          //     header/menu/account/pricing UI flips immediately. For
          //     any OTHER user, the result message tells the operator
          //     the affected user must refresh their browser (or sign
          //     out / sign in) to see the updated paid status — we
          //     CANNOT touch their session from here.
          // ─────────────────────────────────────────────────────────────
          {
            id: 'sync-membership-by-email',
            label: 'Reconcile a specific paid user by email',
            willCheck:
              'Prompts for a user email (defaults to mskeke2884@gmail.com — Chekesha\'s known paid yearly Founder account) and runs the per-email Stripe → profile reconciliation. Surfaces the EXACT failure category (email-mismatch / payment-under-different-email / subscription-not-active / missing-customer-id / webhook-sync-unavailable / already-in-sync / updated-now).',
            mayChange:
              'profiles.plan, profiles.stripe_customer_id, profiles.stripe_subscription_id for the matching profile row IF Stripe shows a higher plan than the local profile. Never downgrades. Never creates a checkout. Never touches family graph, media, invites, email pipeline, identity, pricing, Stripe checkout, or subscription creation.',
            safety: 'destructive',
            tier: 'suggested',
            confidence: 'high',
            reasoning:
              'The operator reported Chekesha\'s yearly Founder account still shows the "Upgrade" CTA. This repair runs the reconcileEmail() path inside the deployed edge function for the exact email the operator pastes in, then reports the structured result so the failure category (email mismatch, sub not active, etc.) is unambiguous.',
            run: async () => {
              const defaultEmail = 'mskeke2884@gmail.com';
              const raw =
                typeof window !== 'undefined' && typeof window.prompt === 'function'
                  ? window.prompt(
                      'Reconcile paid status for which email?\n\n' +
                        '(Stripe gateway will be queried for this address; local profile will only be UPGRADED if a higher active subscription is found. Never downgrades. Defaults to Chekesha\'s known paid yearly Founder account.)',
                      defaultEmail,
                    )
                  : defaultEmail;
              if (raw === null) return { ok: false, message: 'Cancelled.' };
              const email = String(raw || '').trim().toLowerCase();
              if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                return { ok: false, message: `Invalid email: "${raw}".` };
              }
              try {
                const { data, error } = await supabase.functions.invoke(
                  'sync-membership-from-payments',
                  { body: { email } },
                );
                if (error) {
                  return {
                    ok: false,
                    message:
                      `[webhook-sync-unavailable] Edge function invoke error: ${error.message}. ` +
                      `The reconciliation pipeline could not reach the Stripe gateway. ` +
                      `Check that GATEWAY_API_KEY is set and the function is deployed.`,
                  };
                }
                const r = (data ?? {}) as any;
                if (r.ok === false) {
                  return {
                    ok: false,
                    message: `[webhook-sync-unavailable] Reconciliation returned error: ${r.error || 'unknown'}.`,
                  };
                }

                // Map the edge function's `reason` string to the explicit
                // failure category the operator asked for. This is the
                // diagnostic surface — never a downgrade.
                const reason = String(r.reason || '');
                const baseLine =
                  `email=${email} | matched=${!!r.matched} | plan=${r.plan ?? '—'} | status=${r.status ?? '—'} | ` +
                  `customer=${r.customerId ?? 'none'} | sub=${r.subscriptionId ?? 'none'} | reason=${reason || 'unknown'}`;

                // Successful upgrade just performed by this repair.
                if (r.updatedProfile === true && r.matched && r.plan && r.plan !== 'free') {
                  // If the reconciled email matches the signed-in operator,
                  // refresh their own auth profile so the UI flips
                  // immediately. Otherwise the affected user must sign in
                  // again (we cannot touch a different session from here).
                  const myEmail = (user?.email || '').trim().toLowerCase();
                  let refreshNote = '';
                  if (myEmail && myEmail === email) {
                    try {
                      const { data: refetched } = await supabase
                        .from('profiles')
                        .select('plan, stripe_customer_id, stripe_subscription_id')
                        .eq('id', user?.id ?? '')
                        .maybeSingle();
                      refreshNote =
                        ` Local profile re-read: plan="${refetched?.plan ?? '—'}". ` +
                        `Refresh the page to see the new paid badges everywhere.`;
                    } catch {
                      refreshNote = ' Refresh the page to see the new paid badges everywhere.';
                    }
                  } else {
                    refreshNote =
                      ` That user must sign out + sign in (or refresh their browser) ` +
                      `to see the updated Founder Member / yearly status.`;
                  }
                  return {
                    ok: true,
                    message:
                      `[updated-now] ✓ ${email} → plan="${r.plan}" (${r.status}). ` +
                      `${baseLine}.${refreshNote}`,
                  };
                }

                // Already in sync — Stripe shows paid AND local already paid.
                if (r.matched && r.plan && r.plan !== 'free' && /already-in-sync/.test(reason)) {
                  return {
                    ok: true,
                    message:
                      `[already-in-sync] ${email} is already a paid ${r.plan} member ` +
                      `(${r.status}). Local profile already reflects Founder / yearly. ` +
                      `If the user still sees an "Upgrade" CTA, ask them to sign out + sign in. ` +
                      `${baseLine}.`,
                  };
                }

                // Stripe found a paid subscription, but no local profile
                // exists for that email — payment is under a different
                // email than the user's signup.
                if (r.matched && /no-profile-row-for-email/.test(reason)) {
                  return {
                    ok: false,
                    message:
                      `[payment-under-different-email] Stripe shows ${r.plan} (${r.status}) ` +
                      `for ${email}, BUT no local profile row uses that email. The user likely ` +
                      `signed up with a different email than the one on their Stripe receipt. ` +
                      `Ask them which email is on their Stripe receipt and re-run with that. ` +
                      `${baseLine}.`,
                  };
                }

                // No Stripe customer record at all.
                if (/no-stripe-customer/.test(reason)) {
                  return {
                    ok: false,
                    message:
                      `[email-mismatch / missing-customer-id] No Stripe customer found for ${email}. ` +
                      `Either (a) the payment was made under a different email — check the Stripe ` +
                      `receipt and re-run with that email, or (b) the payment never reached Stripe. ` +
                      `${baseLine}.`,
                  };
                }

                // Customer exists but no qualifying active subscription.
                if (/no-active-sub|status=/.test(reason)) {
                  return {
                    ok: false,
                    message:
                      `[subscription-not-active] Stripe customer ${r.customerId} exists for ${email}, ` +
                      `but has no active/trialing/past_due subscription on a recognized KinsCircle ` +
                      `price. Check FamousPay → Subscriptions for ${email} — the most recent ` +
                      `subscription may be canceled, incomplete, or on an unrecognized price ID. ` +
                      `${baseLine}.`,
                  };
                }

                // Customer exists, paid, but on an unrecognized price ID.
                if (/unrecognized-price/.test(reason)) {
                  return {
                    ok: false,
                    message:
                      `[subscription-not-active] Stripe customer ${r.customerId} has an active ` +
                      `subscription, BUT the price ID is not recognized as a KinsCircle plan ` +
                      `(yearly Founder, yearly Standard, or monthly). The reconciler will not ` +
                      `upgrade on an unknown price. Verify the user purchased a KinsCircle plan ` +
                      `(not a different product). ${baseLine}.`,
                  };
                }

                // Gateway returned an error.
                if (/gateway-/.test(reason)) {
                  return {
                    ok: false,
                    message:
                      `[webhook-sync-unavailable] Stripe gateway error: ${reason}. ` +
                      `Stripe was unreachable or returned an error. Try again in a moment; if it ` +
                      `persists, verify GATEWAY_API_KEY in Supabase Edge Function secrets. ` +
                      `${baseLine}.`,
                  };
                }

                // Unhandled / generic — surface the raw structured result
                // so the operator never sees a vague "didn't work" message.
                return {
                  ok: r.matched ? true : false,
                  message:
                    r.matched
                      ? `[matched-no-update] ${baseLine}. No upgrade was needed.`
                      : `[unmatched] ${baseLine}.`,
                };
              } catch (err: any) {
                return {
                  ok: false,
                  message:
                    `[webhook-sync-unavailable] Per-email reconciliation threw: ${err?.message || err}.`,
                };
              }
            },
          },
        ],
      },



      {

        id: 'tree',
        label: 'Family tree loading',
        description: 'Reads (count-only) the family_graph_members table for this owner.',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          if (!userId) return { status: 'fail', detail: 'No user id.' };
          const { count, error } = await supabase
            .from('family_graph_members')
            .select('*', { count: 'exact', head: true })
            .eq('owner_user_id', userId)
            .is('deleted_at', null);
          if (error) return { status: 'fail', detail: error.message };
          return { status: 'pass', detail: `${count ?? 0} relatives loaded.` };
        },
        repairs: [
          {
            id: 'rebuild-tree',
            label: 'Rebuild tree view from existing parent_ids',
            willCheck: 'Re-reads every relative for this owner and re-derives the tree layout from the existing parent_ids column.',
            mayChange: 'Nothing in the database. Only the in-memory tree projection is rebuilt for the next render.',
            safety: 'safe',
            run: async () => {
              if (!userId) return { ok: false, message: 'No user id.' };
              const { data, error } = await supabase
                .from('family_graph_members')
                .select('id, parent_ids, name')
                .eq('owner_user_id', userId)
                .is('deleted_at', null);
              if (error) return { ok: false, message: error.message };
              const total = data?.length ?? 0;
              const linked = (data ?? []).filter((r) => Array.isArray(r.parent_ids) && r.parent_ids.length > 0).length;
              try {
                Object.keys(localStorage).forEach((k) => {
                  if (k.startsWith('kinscircle.tree.cache.')) localStorage.removeItem(k);
                });
              } catch {
                /* non-fatal */
              }
              return {
                ok: true,
                message: `Rebuilt projection: ${total} relatives, ${linked} with parent links.`,
              };
            },
          },
          {
            id: 'refresh-tree-data',
            label: 'Refresh data',
            willCheck: 'Issues a fresh count(*) read against family_graph_members for this owner to confirm connectivity.',
            mayChange: 'Nothing — read-only count query.',
            safety: 'safe',
            run: async () => {
              if (!userId) return { ok: false, message: 'No user id.' };
              const { count, error } = await supabase
                .from('family_graph_members')
                .select('*', { count: 'exact', head: true })
                .eq('owner_user_id', userId)
                .is('deleted_at', null);
              if (error) return { ok: false, message: error.message };
              return { ok: true, message: `Re-read complete: ${count ?? 0} relative(s) visible.` };
            },
          },
        ],
      },

      {
        id: 'add-member',
        label: 'Add member flow',
        description: 'Probes write permissions on family_graph_members WITHOUT inserting a row.',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          if (!userId) return { status: 'fail', detail: 'No user id.' };
          // Strictly read-only RLS probe: confirm the table is reachable for this owner.
          const { error } = await supabase
            .from('family_graph_members')
            .select('id', { head: true, count: 'exact' })
            .eq('owner_user_id', userId)
            .is('deleted_at', null)
            .limit(1);
          if (error) return { status: 'fail', detail: error.message };
          return { status: 'pass', detail: 'Add Member endpoint reachable.' };
        },
        repairs: [
          {
            id: 'recheck-add-perms',
            label: 'Refresh data',
            willCheck: 'Re-issues the read-only RLS probe to family_graph_members to confirm the user can still reach the table.',
            mayChange: 'Nothing — no insert/update/delete is performed. No relatives are created or modified.',
            safety: 'safe',
            run: async () => {
              if (!userId) return { ok: false, message: 'No user id.' };
              const { error } = await supabase
                .from('family_graph_members')
                .select('id', { head: true, count: 'exact' })
                .eq('owner_user_id', userId)
                .is('deleted_at', null)
                .limit(1);
              if (error) return { ok: false, message: error.message };
              return { ok: true, message: 'RLS probe ok — Add Member endpoint reachable.' };
            },
          },
        ],
      },

      {
        id: 'edit-relationship',
        label: 'Edit relationship flow',
        description: 'Confirms the strict connector validation rules are loaded.',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          // The validation lives in SetRelationshipModal; here we just ensure
          // the kinship engine is importable and not throwing at module level.
          try {
            const sample = kinshipLabel(
              { id: 'probe', name: 'Probe', parent_ids: [] } as any,
              null,
              [],
            );
            return {
              status: 'pass',
              detail: `Connector validator + kinship engine ready (sample="${sample}").`,
            };
          } catch (err: any) {
            return { status: 'fail', detail: err?.message || 'Kinship engine threw on probe.' };
          }
        },
        repairs: [
          {
            id: 'rerun-validation',
            label: 'Re-run relationship validation',
            willCheck: 'Walks every relative and verifies that each parent_id points to a relative whose type is a structurally valid connector.',
            mayChange: 'Nothing. This is a read-only audit — invalid links are reported, never auto-rewritten.',
            safety: 'safe',

            run: async () => {
              if (!userId) return { ok: false, message: 'No user id.' };
              const { data, error } = await supabase
                .from('family_graph_members')
                .select('id, name, relationship_type, parent_ids')
                .eq('owner_user_id', userId)
                .is('deleted_at', null);
              if (error) return { ok: false, message: error.message };
              const byId = new Map<string, any>((data ?? []).map((r) => [r.id, r]));
              let issues = 0;
              const allowed: Record<string, string[]> = {
                cousin: ['aunt-uncle'],
                'cousin-child': ['cousin'],
                'niece-nephew': ['sibling'],
                grandchild: ['child'],
                'great-grandchild': ['grandchild'],
                'aunt-uncle': ['mother', 'father', 'step-parent', 'adopted-parent', 'grandparent'],
                grandparent: ['mother', 'father', 'step-parent', 'adopted-parent'],
                'great-grandparent': ['grandparent'],
                sibling: ['mother', 'father', 'step-parent', 'adopted-parent'],
              };
              (data ?? []).forEach((row) => {
                const rule = allowed[row.relationship_type as string];
                if (!rule || !Array.isArray(row.parent_ids) || row.parent_ids.length === 0) return;
                const ok = (row.parent_ids as string[]).every((pid) => {
                  const parent = byId.get(pid);
                  if (!parent) return true; // viewer or external — skip
                  return rule.includes(parent.relationship_type);
                });
                if (!ok) issues += 1;
              });
              return {
                ok: true,
                message: issues === 0
                  ? 'All relationship connectors are structurally valid.'
                  : `Audit complete — ${issues} relative(s) have a connector that does not match the strict rules. They were NOT modified; please review them in the tree.`,
              };
            },
          },
          {
            id: 'reconnect-parents',
            label: 'Find missing parent links',
            willCheck: 'Lists relatives whose relationship_type implies a parent connector but whose parent_ids is empty.',
            mayChange: 'Nothing — orphans are reported only. Linking a parent must still be done from the Edit modal so validation runs.',
            safety: 'safe',
            tier: 'suggested',
            confidence: 'medium',
            reasoning: 'Identified by relationship_type that structurally requires a parent connector but has an empty parent_ids array.',
            run: async () => {
              if (!userId) return { ok: false, message: 'No user id.' };

              const { data, error } = await supabase
                .from('family_graph_members')
                .select('id, name, relationship_type, parent_ids')
                .eq('owner_user_id', userId)
                .is('deleted_at', null);
              if (error) return { ok: false, message: error.message };
              const needsParent = new Set([
                'cousin',
                'cousin-child',
                'niece-nephew',
                'grandchild',
                'great-grandchild',
                'aunt-uncle',
                'grandparent',
                'great-grandparent',
                'sibling',
              ]);
              const orphans = (data ?? []).filter(
                (r) =>
                  needsParent.has(r.relationship_type as string) &&
                  (!Array.isArray(r.parent_ids) || r.parent_ids.length === 0),
              );
              return {
                ok: true,
                message: orphans.length === 0
                  ? 'No orphaned connectors found.'
                  : `${orphans.length} relative(s) need a parent link: ${orphans.slice(0, 5).map((o) => o.name).join(', ')}${orphans.length > 5 ? '…' : ''}`,
              };
            },
          },

        ],
      },
      {
        // ─────────────────────────────────────────────────────────────────
        // Phase 15 — Relationship Labels Diagnostic (SURGICAL REPAIR).
        //
        // ROOT CAUSE of the prior "Unknown relation" warning:
        //   The synthetic probe fixture used snake_case DB-row keys
        //   (parent_ids / relationship_type) but `kinshipLabel` →
        //   `computeKinship` reads the camelCase GraphMember shape
        //   (parentIds / relationship). With every walked field
        //   undefined and the target row missing from the members
        //   array, the engine landed on its safe-fallback "unknown"
        //   branch (FamilyGraphContext.tsx line 4047-4052). The engine
        //   was correct; the fixture was speaking a different language.
        //
        // SURGICAL FIX — fixture-only, no engine change:
        //   • Switch fixture to canonical GraphMember shape
        //     (parentIds + relationship), matching the runtime
        //     contract used everywhere else in the app.
        //   • Include the TARGET row inside the members array so
        //     `byId.get(targetId)` resolves at the engine entrypoint.
        //   • Add the paternal parent anchor the operator just wired
        //     up so we exercise the FULL-sibling code path (viewer +
        //     target both share BOTH known parents → 'Sibling').
        //   • Never return the bare string "Unknown relation" again —
        //     on any unresolved path, surface the exact diagnostic
        //     trace (source / target / connector / expected / actual)
        //     so the operator can see why instantly.
        //
        // SCOPE LOCKS — strictly preserved:
        //   • Real production data (every `family_graph_members` row,
        //     every parent_ids array, every spouse_id, every
        //     claimed_by_user_id, every relationship_type) is NEVER
        //     touched.
        //   • The kinship engine (`computeKinship`, `kinshipLabel`,
        //     `viewerRelationshipLabel`, `inferRelationships`,
        //     `projectMembersForViewer`) is NEVER modified — no rule
        //     added, no rule removed, no path semantics changed.
        //   • Parents / grandparents / great-grandparents / siblings /
        //     half-siblings / aunts-uncles / nieces-nephews / cousins
        //     (with degree + removal) / spouses / in-laws / step
        //     relationships / extended-relative path fallback all
        //     continue to behave exactly as before.
        //   • Tree rendering, spouse linking, uploads, invites,
        //     membership sync, email notifications, identity
        //     centering, graph mutation gateway, RLS, schema, storage
        //     buckets, and every existing relationship placement are
        //     all untouched.
        // ─────────────────────────────────────────────────────────────────
        id: 'labels',
        label: 'Relationship labels',
        description: 'Runs the kinship label engine against a synthetic tree (full-sibling case with both maternal + paternal anchors) and verifies the derived label.',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          // Synthetic fixture — canonical GraphMember shape.
          //
          // Tree:
          //   mom ──┐
          //         ├── self  (viewer)
          //   dad ──┤
          //         └── b     (target — full sibling of self)
          //
          // Expected: full-sibling case → engine returns "Sibling".
          const mom = {
            id: 'mom',
            name: 'Maternal Anchor',
            parentIds: [],
            relationship: 'mother',
            side: 'mother',
          } as any;
          const dad = {
            id: 'dad',
            name: 'Paternal Anchor',
            parentIds: [],
            relationship: 'father',
            side: 'father',
          } as any;
          const self = {
            id: 'self',
            name: 'Me',
            parentIds: ['mom', 'dad'],
            relationship: 'unknown',
            side: 'self',
          } as any;
          const target = {
            id: 'b',
            name: 'Sibling Probe',
            parentIds: ['mom', 'dad'],
            relationship: 'sibling',
            side: 'self',
          } as any;
          const fixtureMembers = [mom, dad, self, target];
          try {
            const lbl = kinshipLabel(target, 'self', fixtureMembers);
            const expected = 'Sibling';
            if (!lbl || /unknown/i.test(lbl)) {
              // Surface the EXACT unresolved trace — never the bare
              // generic "Unknown relation" wording. Operators see the
              // source row, target row, connector path, expected
              // label, and the actual returned string so they can
              // pinpoint which mapping is missing without guessing.
              const trace =
                `source=self(parentIds=[mom,dad]) ` +
                `target=b(parentIds=[mom,dad], relationship=sibling) ` +
                `connector=shared-parents(mom,dad) ` +
                `expected="${expected}" ` +
                `actual="${lbl ?? 'null'}"`;
              return {
                status: 'fail',
                detail: `Kinship engine could not resolve a structurally provable Sibling. ${trace}`,
              };
            }
            if (lbl !== expected) {
              return {
                status: 'warn',
                detail: `Engine returned "${lbl}" — expected "${expected}". Source=self target=b connector=shared-parents(mom,dad).`,
              };
            }
            return {
              status: 'pass',
              detail: `Sample sibling label resolved to "${lbl}" (full-sibling via shared maternal + paternal anchors).`,
            };
          } catch (err: any) {
            return { status: 'fail', detail: err?.message || 'Label engine threw.' };
          }
        },
        repairs: [
          {
            id: 'retest-labels',
            label: 'Re-run relationship validation',
            willCheck: 'Re-runs the synthetic kinship probe (canonical GraphMember shape, both maternal + paternal anchors) against the engine to verify it can still resolve a sibling label.',
            mayChange: 'Nothing — the kinship engine is pure / deterministic and has no side effects.',
            safety: 'safe',
            run: async () => {
              const mom = { id: 'mom', name: 'Maternal Anchor', parentIds: [], relationship: 'mother', side: 'mother' } as any;
              const dad = { id: 'dad', name: 'Paternal Anchor', parentIds: [], relationship: 'father', side: 'father' } as any;
              const self = { id: 'self', name: 'Me', parentIds: ['mom', 'dad'], relationship: 'unknown', side: 'self' } as any;
              const target = { id: 'b', name: 'Sibling Probe', parentIds: ['mom', 'dad'], relationship: 'sibling', side: 'self' } as any;
              try {
                const lbl = kinshipLabel(target, 'self', [mom, dad, self, target]);
                if (!lbl) return { ok: false, message: 'Label engine returned empty.' };
                if (/unknown/i.test(lbl)) {
                  return {
                    ok: false,
                    message:
                      `Engine returned "${lbl}" for source=self target=b connector=shared-parents(mom,dad). ` +
                      `Expected="Sibling". The synthetic fixture is well-formed; review the kinship engine's full-sibling branch in computeKinship() if this persists.`,
                  };
                }
                return { ok: true, message: `Engine ok — sample resolved to "${lbl}" (expected="Sibling").` };
              } catch (err: any) {
                return { ok: false, message: err?.message || 'Label engine threw.' };
              }
            },
          },
        ],
      },

      {
        id: 'invites',
        label: 'Invite links',
        description: 'Counts existing invite rows for this user (read-only).',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          if (!userId) return { status: 'fail', detail: 'No user id.' };
          const { count, error } = await supabase
            .from('invites')
            .select('*', { count: 'exact', head: true })
            .eq('inviter_user_id', userId);
          if (error) return { status: 'fail', detail: error.message };
          return { status: 'pass', detail: `${count ?? 0} invite(s) on record.` };
        },
        repairs: [
          {
            id: 'recheck-invite-routing',
            label: 'Re-check invite link routing',
            willCheck: 'Verifies that the /invite/:token route is registered and that the invites table is reachable for this user.',
            mayChange: 'Nothing — invite rows, tokens, and recipient emails are NOT modified, regenerated, or revoked.',
            safety: 'safe',
            run: async () => {
              if (!userId) return { ok: false, message: 'No user id.' };
              const routeOk = typeof window !== 'undefined' && /\/invite\//.test('/invite/probe');
              const { count, error } = await supabase
                .from('invites')
                .select('*', { count: 'exact', head: true })
                .eq('inviter_user_id', userId);
              if (error) return { ok: false, message: error.message };
              return {
                ok: true,
                message: `Invite router ${routeOk ? 'available' : 'unavailable'}; ${count ?? 0} invite row(s) reachable.`,
              };
            },
          },
        ],
      },

      {
        id: 'photo-upload',
        label: 'Photo upload',
        description: 'Verifies the family-media bucket is reachable for listing.',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          const { error } = await supabase.storage.from('family-media').list('', { limit: 1 });
          if (error) return { status: 'fail', detail: error.message };
          return { status: 'pass', detail: 'family-media bucket reachable.' };
        },
        repairs: [
          {
            id: 'reupload-perms',
            label: 'Re-test upload permissions',
            willCheck: 'Lists the first object in the family-media bucket as the current user to confirm RLS / storage policy.',
            mayChange: 'Nothing — listing is read-only and does not upload, move, or delete any file.',
            safety: 'safe',
            run: async () => {
              const { data, error } = await supabase.storage.from('family-media').list('', { limit: 1 });
              if (error) return { ok: false, message: error.message };
              return { ok: true, message: `Storage probe ok — ${data?.length ?? 0} object(s) visible at bucket root.` };
            },
          },
        ],

      },
      {
        id: 'video-upload',
        label: 'Video upload',
        description: 'Same family-media bucket — videos and photos share the same path policy.',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          const { error } = await supabase.storage.from('family-media').list('', { limit: 1 });
          if (error) return { status: 'fail', detail: error.message };
          return { status: 'pass', detail: 'Video uploads share the same bucket and policy.' };
        },
        repairs: [
          {
            id: 'reupload-video-perms',
            label: 'Re-test upload permissions',
            willCheck: 'Lists the family-media bucket as the current user to verify the same RLS / storage policy that videos use.',
            mayChange: 'Nothing — listing is read-only and never uploads, moves, or deletes any video file.',
            safety: 'safe',
            run: async () => {
              const { data, error } = await supabase.storage.from('family-media').list('', { limit: 1 });
              if (error) return { ok: false, message: error.message };
              return { ok: true, message: `Storage probe ok — ${data?.length ?? 0} object(s) at bucket root.` };
            },
          },
        ],
      },
      {
        id: 'memories',
        label: 'Memory archive',
        description: 'Counts memory rows for this owner (read-only).',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          if (!userId) return { status: 'fail', detail: 'No user id.' };
          const { count, error } = await supabase
            .from('family_graph_memories')
            .select('*', { count: 'exact', head: true })
            .eq('owner_user_id', userId);
          if (error) return { status: 'fail', detail: error.message };
          return { status: 'pass', detail: `${count ?? 0} memory entries.` };
        },
        repairs: [
          {
            id: 'refresh-memories',
            label: 'Refresh data',
            willCheck: 'Re-reads the count of family_graph_memories rows for this owner.',
            mayChange: 'Nothing — no memory, photo, or video is created, deleted, or moved.',
            safety: 'safe',
            run: async () => {
              if (!userId) return { ok: false, message: 'No user id.' };
              const { count, error } = await supabase
                .from('family_graph_memories')
                .select('*', { count: 'exact', head: true })
                .eq('owner_user_id', userId);
              if (error) return { ok: false, message: error.message };
              return { ok: true, message: `Memory archive ok — ${count ?? 0} entries visible.` };
            },
          },
        ],
      },
      {
        id: 'activity',
        label: 'Activity feed',
        description: 'Confirms the activity feed has a recent source row from members or memories.',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          if (!userId) return { status: 'fail', detail: 'No user id.' };
          const { data, error } = await supabase
            .from('family_graph_members')
            .select('id, created_at')
            .eq('owner_user_id', userId)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .limit(1);
          if (error) return { status: 'fail', detail: error.message };
          if (!data || data.length === 0) {
            return { status: 'warn', detail: 'No activity yet — feed will be empty.' };
          }
          return { status: 'pass', detail: 'Activity feed has data.' };
        },
        repairs: [
          {
            id: 'refresh-activity',
            label: 'Refresh data',
            willCheck: 'Re-fetches the most recent member row to repopulate the activity feed source.',
            mayChange: 'Nothing — no row is created, edited, or deleted.',
            safety: 'safe',
            run: async () => {
              if (!userId) return { ok: false, message: 'No user id.' };
              const { data, error } = await supabase
                .from('family_graph_members')
                .select('id, created_at')
                .eq('owner_user_id', userId)
                .is('deleted_at', null)
                .order('created_at', { ascending: false })
                .limit(1);
              if (error) return { ok: false, message: error.message };
              return {
                ok: true,
                message: data && data.length > 0
                  ? 'Activity feed source is reachable and has at least one entry.'
                  : 'Activity feed source is reachable but currently empty — add a relative to populate it.',
              };
            },
          },
        ],
      },
      {
        // ─────────────────────────────────────────────────────────────────
        // Phase 14 — Email Notifications Diagnostic Probe
        //
        // Replaces the previous generic "Failed to send a request to the
        // Edge Function" probe with a categorized one that distinguishes:
        //
        //   • function-missing       → edge function is not deployed
        //   • secret-missing         → RESEND_API_KEY not in backend secrets
        //   • sender-not-verified    → Resend says kinscircleapp.com is not
        //                              verified yet (or wrong from address)
        //   • resend-rejected        → Resend rejected for other reasons
        //                              (invalid key, rate-limit, …)
        //   • function-error         → 5xx from a deployed function
        //   • function-unreachable   → network / project paused / cold-start
        //
        // The probe ALWAYS sends `dryRun: true` so no real email is ever
        // delivered. It also performs a parallel direct HTTPS fetch to the
        // function URL so it can read the actual HTTP status (404 = not
        // deployed) that supabase-js otherwise hides as "Failed to send a
        // request to the Edge Function".
        //
        // Strictly read-only. No edge function code is created or modified
        // here. No DB write. No schema change. No effect on payments,
        // pricing, uploads, family graph, invites, identity, or RLS.
        // ─────────────────────────────────────────────────────────────────
        id: 'email',
        label: 'Email notifications',
        description: `Pings the ${EMAIL_FUNCTION_NAME} edge function in dry-run mode and classifies the EXACT failure (function missing / secret missing / sender unverified / Resend rejected / network).`,
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          const r = await runEmailDiagnostic();
          // Headline is "<detail>  Next step: <nextStep>" so the operator
          // sees the exact manual fix without having to expand anything.
          const headline = r.nextStep
            ? `${r.detail}  Next step: ${r.nextStep}`
            : r.detail;
          return { status: r.severity, detail: headline };
        },
        repairs: [
          {
            id: 'recheck-email-pipeline',
            label: 'Re-run categorized email diagnostic',
            willCheck: `Re-runs the categorized probe against ${EMAIL_FUNCTION_NAME}: supabase-js invoke + direct HTTPS fetch (dryRun: true). Reports the EXACT cause (function missing / RESEND_API_KEY missing / sender domain not verified / Resend rejected / function 5xx / unreachable) and the EXACT manual fix.`,
            mayChange: 'Nothing. The probe sends dryRun: true so no email is ever delivered, and no DB row is touched.',
            safety: 'safe',
            run: async () => {
              const r = await runEmailDiagnostic();
              if (r.severity === 'pass') {
                return {
                  ok: true,
                  message: `${r.detail}${r.senderSeen ? ` (sender: ${r.senderSeen})` : ''}`,
                };
              }
              // Surface the exact cause + next step in the toast so the
              // operator gets actionable info without expanding the card.
              return {
                ok: false,
                message: r.nextStep
                  ? `[${r.category}] ${r.detail}  Next step: ${r.nextStep}`
                  : `[${r.category}] ${r.detail}`,
              };
            },
          },
          {
            id: 'open-resend-secrets',
            label: 'Where do I paste the Resend API key?',
            willCheck: 'Opens the Supabase Edge Function secrets manager in a new tab so you can paste your RESEND_API_KEY.',
            mayChange: 'Nothing in this app. You will paste a secret directly into Supabase. The key never appears in this app\'s frontend code.',
            safety: 'safe',
            run: async () => {
              const url =
                'https://supabase.com/dashboard/project/vadsiqsuomscnuiuyreo/settings/functions';
              try {
                if (typeof window !== 'undefined') {
                  window.open(url, '_blank', 'noopener,noreferrer');
                }
              } catch {
                /* non-fatal — fall through to the message below */
              }
              return {
                ok: true,
                message:
                  `Opened Supabase → Project Settings → Edge Functions → Secrets. ` +
                  `Add a secret named EXACTLY: RESEND_API_KEY with the value from ` +
                  `https://resend.com/api-keys (server-scoped key). Verified senders: ` +
                  `${CANONICAL_SENDER}. If the popup was blocked, navigate manually to: ${url}`,
              };
            },
          },
        ],
      },

      {
        id: 'birthdays',
        label: 'Birthday reminders',
        description: 'Counts relatives that have a usable birthday set.',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          if (!userId) return { status: 'fail', detail: 'No user id.' };
          const { data, error } = await supabase
            .from('family_graph_members')
            .select('id, birth_date, birth_month, birth_day')
            .eq('owner_user_id', userId)
            .is('deleted_at', null);
          if (error) return { status: 'fail', detail: error.message };
          const withBirthday = (data ?? []).filter(
            (r: any) => r.birth_date || (r.birth_month && r.birth_day),
          ).length;
          if (withBirthday === 0) {
            return { status: 'warn', detail: 'No relatives have a birthday set yet.' };
          }
          return { status: 'pass', detail: `${withBirthday} relative(s) ready for birthday reminders.` };
        },
        repairs: [
          {
            id: 'recheck-birthday-setup',
            label: 'Re-check notification/birthday reminder setup',
            willCheck: 'Counts how many relatives have a usable birth_date or birth_month+birth_day so the reminder job can find them.',
            mayChange: 'Nothing — birthdays themselves are NOT changed, filled in, or guessed. This is a SELECT only.',
            safety: 'safe',
            run: async () => {
              if (!userId) return { ok: false, message: 'No user id.' };
              const { data, error } = await supabase
                .from('family_graph_members')
                .select('id, birth_date, birth_month, birth_day')
                .eq('owner_user_id', userId)
                .is('deleted_at', null);
              if (error) return { ok: false, message: error.message };
              const withBirthday = (data ?? []).filter(
                (r: any) => r.birth_date || (r.birth_month && r.birth_day),
              ).length;
              return {
                ok: true,
                message: `${withBirthday} of ${data?.length ?? 0} relative(s) have a birthday set — reminder job will target them.`,
              };
            },
          },
        ],
      },

      {
        id: 'mobile',
        label: 'Mobile layout',
        description: 'Detects whether the current viewport renders the mobile layout correctly.',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          const w = typeof window !== 'undefined' ? window.innerWidth : 0;
          if (w === 0) return { status: 'warn', detail: 'No window — running on server?' };
          if (w < 640) {
            return { status: 'pass', detail: `Viewport ${w}px — mobile layout active.` };
          }
          return { status: 'pass', detail: `Viewport ${w}px — mobile breakpoints compiled (sm/md/lg).` };
        },
      },
      {
        id: 'desktop',
        label: 'Desktop layout',
        description: 'Detects whether the current viewport renders the desktop layout correctly.',
        status: 'idle',
        detail: '',
        lastChecked: null,
        run: async () => {
          const w = typeof window !== 'undefined' ? window.innerWidth : 0;
          if (w === 0) return { status: 'warn', detail: 'No window — running on server?' };
          if (w >= 1024) {
            return { status: 'pass', detail: `Viewport ${w}px — full desktop layout active.` };
          }
          return { status: 'pass', detail: `Viewport ${w}px — desktop breakpoints compiled, will activate ≥1024px.` };
        },
      },
    ];
  }, [user?.id, plan, isOwner, isAdmin]);

  // Initialize checks once we know who the user is
  useEffect(() => {
    if (!isAuthenticated) return;
    setChecks(buildChecks());
  }, [isAuthenticated, buildChecks]);

  // -------------------------------------------------------------------------
  // Run checks (one or all)
  // -------------------------------------------------------------------------
  const runOne = useCallback(
    async (id: string) => {
      const target = checks.find((c) => c.id === id);
      if (!target) return;
      setChecks((prev) => prev.map((c) => (c.id === id ? { ...c, status: 'running' } : c)));
      try {
        const res = await target.run();
        setChecks((prev) =>
          prev.map((c) =>
            c.id === id
              ? { ...c, status: res.status, detail: res.detail, lastChecked: Date.now() }
              : c,
          ),
        );
        if (res.status === 'fail' || res.status === 'warn') {
          appendLog({
            feature: target.label,
            failure: res.detail || res.status,
            repair: null,
            succeeded: null,
          });
        }
      } catch (err: any) {
        setChecks((prev) =>
          prev.map((c) =>
            c.id === id
              ? { ...c, status: 'fail', detail: err?.message || 'Check threw.', lastChecked: Date.now() }
              : c,
          ),
        );
        appendLog({
          feature: target.label,
          failure: err?.message || 'Check threw.',
          repair: null,
          succeeded: null,
        });
      }
    },
    [checks, appendLog],
  );

  const runAll = useCallback(async () => {
    setRunning(true);
    try {
      // Mark all as running
      setChecks((prev) => prev.map((c) => ({ ...c, status: 'running' as CheckStatus })));
      // Run sequentially so errors don't pile up on the network
      for (const c of checks) {
        try {
          const res = await c.run();
          setChecks((prev) =>
            prev.map((x) =>
              x.id === c.id
                ? { ...x, status: res.status, detail: res.detail, lastChecked: Date.now() }
                : x,
            ),
          );
          if (res.status === 'fail' || res.status === 'warn') {
            appendLog({
              feature: c.label,
              failure: res.detail || res.status,
              repair: null,
              succeeded: null,
            });
          }
        } catch (err: any) {
          setChecks((prev) =>
            prev.map((x) =>
              x.id === c.id
                ? {
                    ...x,
                    status: 'fail',
                    detail: err?.message || 'Check threw.',
                    lastChecked: Date.now(),
                  }
                : x,
            ),
          );
          appendLog({
            feature: c.label,
            failure: err?.message || 'Check threw.',
            repair: null,
            succeeded: null,
          });
        }
      }
    } finally {
      setRunning(false);
    }
  }, [checks, appendLog]);

  // -------------------------------------------------------------------------
  // Repair handlers — 3-tier system
  //   AUTO       → runs instantly, no dialog (read-only / local-only).
  //   SUGGESTED  → opens single-tap confirm dialog with reasoning + confidence.
  //   PROTECTED  → opens hard-warning confirm dialog (none ship by default).
  // -------------------------------------------------------------------------
  const executeRepair = useCallback(
    async (
      featureId: string,
      feature: string,
      action: RepairAction,
      detectedIssue: string,
      approved: boolean,
    ) => {
      let res: { ok: boolean; message: string };
      try {
        res = await action.run();
      } catch (err: any) {
        res = { ok: false, message: err?.message || 'Repair threw.' };
      }
      appendLog({
        feature,
        failure: detectedIssue || action.label,
        repair: action.label,
        succeeded: res.ok,
        error: res.ok ? undefined : res.message,
        tier: action.tier ?? 'auto',
        confidence: action.confidence,
        approved,
      });
      if (res.ok) {
        toast.success(res.message);
        await runOne(featureId);
      } else {
        toast.error(res.message);
      }
      return res;
    },
    [appendLog, runOne],
  );

  const requestRepair = (
    featureId: string,
    feature: string,
    action: RepairAction,
    detectedIssue: string,
  ) => {
    const tier = action.tier ?? 'auto';
    if (tier === 'auto') {
      // AUTO tier — run instantly, no confirmation. Read-only / local-only only.
      void executeRepair(featureId, feature, action, detectedIssue, true);
      return;
    }
    // SUGGESTED or PROTECTED — open confirm dialog.
    setConfirmRepair({ featureId, feature, action, detectedIssue });
  };

  const runRepair = async () => {
    if (!confirmRepair) return;
    setRepairing(true);
    await executeRepair(
      confirmRepair.featureId,
      confirmRepair.feature,
      confirmRepair.action,
      confirmRepair.detectedIssue,
      true,
    );
    setRepairing(false);
    setConfirmRepair(null);
  };



  // -------------------------------------------------------------------------
  // Cache helpers (safe — only clears local browser cache)
  // -------------------------------------------------------------------------
  const clearLocalCache = useCallback(() => {
    try {
      const keep = new Set([
        'sb-access-token',
        'sb-refresh-token',
        'supabase.auth.token',
      ]);
      const removed: string[] = [];
      Object.keys(localStorage).forEach((k) => {
        if (keep.has(k) || k.startsWith('sb-')) return;
        if (k === LOG_KEY) return;
        removed.push(k);
        localStorage.removeItem(k);
      });
      try {
        sessionStorage.clear();
      } catch {
        /* non-fatal */
      }
      toast.success(`Cleared ${removed.length} local cache key(s). Auth + recovery log preserved.`);
      appendLog({
        feature: 'Local cache',
        failure: 'Manual clear',
        repair: `Cleared ${removed.length} key(s)`,
        succeeded: true,
      });
    } catch (err: any) {
      toast.error(err?.message || 'Could not clear local cache.');
    }
  }, [appendLog]);

  // -------------------------------------------------------------------------
  // Summary counts
  // -------------------------------------------------------------------------
  const summary = useMemo(() => {
    const out = { pass: 0, warn: 0, fail: 0, idle: 0 };
    checks.forEach((c) => {
      if (c.status === 'pass') out.pass += 1;
      else if (c.status === 'warn') out.warn += 1;
      else if (c.status === 'fail') out.fail += 1;
      else out.idle += 1;
    });
    return out;
  }, [checks]);

  // -------------------------------------------------------------------------
  // Guards
  // -------------------------------------------------------------------------
  if (
    authLoading ||
    commandCenter.loading
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-fuchsia-600" />
      </div>
    );
  }

  if (
    !isAuthenticated ||
    !commandCenter.allowed
  ) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <ShieldCheck className="w-10 h-10 text-slate-400 mb-3" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Command Center authority required
        </h1>
        <p className="text-slate-600 mb-6 max-w-md">
          System Health and governed repair controls are restricted to active Command Center principals.
        </p>
        <Button onClick={() => navigate('/')} className="rounded-full">
          Back to home
        </Button>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <button onClick={() => navigate('/')} aria-label="KinsCircle home">
            <Logo />
          </button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/settings')}
              className="rounded-full gap-1.5"
            >
              <ArrowLeft className="w-4 h-4" />
              Account
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {/* Hero */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 text-xs font-semibold mb-2">
              <ShieldCheck className="w-3.5 h-3.5" />
              Admin only
            </div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Activity className="w-8 h-8 text-fuchsia-600" />
              System Health
            </h1>
            <p className="text-slate-600 mt-1 max-w-2xl">
              Lightweight, read-only diagnostics for KinsCircle. Repairs are confirmation-gated
              and never modify your relatives, media, or schema without your explicit approval.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={runAll}
              disabled={running || checks.length === 0}
              className="rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white gap-2"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
              Run all checks
            </Button>
            <Button variant="outline" onClick={clearLocalCache} className="rounded-full gap-2">
              <Trash2 className="w-4 h-4" />
              Clear local cache
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid sm:grid-cols-4 gap-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-xs uppercase tracking-wide text-emerald-700">Working</div>
            <div className="text-2xl font-bold text-emerald-800">{summary.pass}</div>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-xs uppercase tracking-wide text-amber-700">Warnings</div>
            <div className="text-2xl font-bold text-amber-800">{summary.warn}</div>
          </div>
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
            <div className="text-xs uppercase tracking-wide text-rose-700">Failing</div>
            <div className="text-2xl font-bold text-rose-800">{summary.fail}</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Not yet checked</div>
            <div className="text-2xl font-bold text-slate-700">{summary.idle}</div>
          </div>
        </div>

        {/* ── Intelligence Layer V1 — Tree Intelligence Check ──────────
            Owner / admin only. Hidden by default behind the
            ENABLE_KINSCIRCLE_INTELLIGENCE flag. Pure additive UI; never
            mutates anything. Wrapped in its own FamilyGraphProvider so
            the card can read the loaded family graph without forcing
            the rest of SystemHealth into the provider. See
            STABILITY.md §9. */}
        <FamilyGraphProvider>
          <TreeIntelligenceCard />
        </FamilyGraphProvider>

        {/* ── Upload Reliability Layer V1 — Diagnostics ────────────────
            Owner / admin only. Hidden by default behind the
            ENABLE_UPLOAD_RELIABILITY flag. Pure additive UI; never
            mutates uploads. Reports only on uploads that flow through
            the new opt-in `uploadQueue` manager — the legacy
            MediaUploadModal continues to use its existing pipeline.
            See STABILITY.md §10. */}
        <UploadDiagnosticsCard />

        {/* ── Phase 6A — AI Memory Story Writer + Timeline Intelligence V1
            Owner / admin only. Hidden by default behind the
            ENABLE_AI_DIAGNOSTICS flag. Pure additive UI; never mutates
            anything. Reads only the local AI usage log. See
            STABILITY.md §11. */}
        <AIDiagnosticsCard />


        {/* Checks */}

        <div className="space-y-3">
          {checks.map((c) => (
            <Card key={c.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      {c.label}
                    </CardTitle>
                    <CardDescription className="mt-1">{c.description}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {statusBadge(c.status)}
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full gap-1.5"
                      onClick={() => runOne(c.id)}
                      disabled={c.status === 'running' || running}
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${c.status === 'running' ? 'animate-spin' : ''}`} />
                      Re-check
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
                  <div className="text-slate-700">
                    {c.detail || <span className="text-slate-400 italic">No diagnostic run yet.</span>}
                  </div>
                  <div className="text-xs text-slate-500">
                    Last checked: {fmtTime(c.lastChecked)}
                  </div>
                </div>
                {c.repairs && c.repairs.length > 0 && (c.status === 'fail' || c.status === 'warn') && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                    {c.repairs.map((r) => {
                      const tier = r.tier ?? 'auto';
                      const tierColor =
                        tier === 'protected'
                          ? 'border-rose-200 text-rose-700 hover:bg-rose-50'
                          : tier === 'suggested'
                          ? 'border-amber-200 text-amber-800 hover:bg-amber-50'
                          : 'border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50';
                      return (
                        <Button
                          key={r.id}
                          variant="outline"
                          size="sm"
                          className={`rounded-full gap-1.5 ${tierColor}`}
                          onClick={() => requestRepair(c.id, c.label, r, c.detail || c.status)}
                        >
                          <Wrench className="w-3.5 h-3.5" />
                          {r.label}
                          {tierBadge(tier)}
                          {r.confidence && confidenceBadge(r.confidence)}
                        </Button>
                      );
                    })}
                  </div>
                )}

              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recovery log */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ScrollText className="w-5 h-5 text-fuchsia-600" />
                  Recovery log
                </CardTitle>
                <CardDescription>
                  The last {MAX_LOG} failures and repair attempts. Stored locally in this browser.
                </CardDescription>
              </div>
              {log.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearLog} className="rounded-full gap-1.5 text-slate-500">
                  <Trash2 className="w-3.5 h-3.5" />
                  Clear log
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {log.length === 0 ? (
              <div className="text-sm text-slate-500 italic">No failures recorded — everything looks healthy.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {log.map((entry) => (
                  <li key={entry.id} className="py-3 flex flex-col sm:flex-row sm:items-start gap-2 text-sm">
                    <div className="text-xs text-slate-500 sm:w-44 flex-shrink-0">
                      {fmtTime(entry.ts)}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{entry.feature}</div>
                      <div className="text-slate-600">{entry.failure}</div>
                      {entry.repair && (
                        <div className="mt-1 text-xs flex flex-wrap items-center gap-1.5">
                          <span className="text-slate-500">Repair attempted:</span>
                          <span className="font-medium text-slate-700">{entry.repair}</span>
                          {tierBadge(entry.tier)}
                          {entry.confidence && confidenceBadge(entry.confidence)}
                          {entry.approved !== undefined && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${entry.approved ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                              {entry.approved ? 'Approved' : 'Auto'}
                            </span>
                          )}
                          {entry.succeeded === true && (
                            <span className="text-emerald-700">✓ succeeded</span>
                          )}
                          {entry.succeeded === false && (
                            <span className="text-rose-700">× failed</span>
                          )}
                          {entry.error && (
                            <span className="w-full mt-0.5 text-rose-600">Error: {entry.error}</span>
                          )}
                        </div>
                      )}

                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Confirmation dialog for any repair */}
      <AlertDialog
        open={!!confirmRepair}
        onOpenChange={(open) => {
          if (!open && !repairing) setConfirmRepair(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-fuchsia-600" />
              {confirmRepair?.action.tier === 'protected' ? 'Protected repair — confirm carefully' : 'Confirm repair'}
              <span className="ml-1">{tierBadge(confirmRepair?.action.tier ?? 'auto')}</span>
              {confirmRepair?.action.confidence && (
                <span>{confidenceBadge(confirmRepair.action.confidence)}</span>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                {confirmRepair?.action.tier === 'protected' && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-800 text-xs font-medium">
                    This is a PROTECTED repair. It can change database records. Review the impact below before confirming.
                  </div>
                )}
                <div>
                  <div className="font-semibold text-slate-700">Detected issue</div>
                  <div className="text-slate-600">{confirmRepair?.detectedIssue || '—'}</div>
                </div>
                {confirmRepair?.action.reasoning && (
                  <div>
                    <div className="font-semibold text-slate-700">Why this fix is suggested</div>
                    <div className="text-slate-600">{confirmRepair.action.reasoning}</div>
                  </div>
                )}
                <div>
                  <div className="font-semibold text-slate-700">What will be checked</div>
                  <div className="text-slate-600">{confirmRepair?.action.willCheck}</div>
                </div>
                <div>
                  <div className="font-semibold text-slate-700">What may change</div>
                  <div className="text-slate-600">{confirmRepair?.action.mayChange}</div>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                  Feature: <span className="font-semibold">{confirmRepair?.feature}</span>
                  <br />
                  Action: <span className="font-semibold">{confirmRepair?.action.label}</span>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={repairing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                runRepair();
              }}
              disabled={repairing}
              className="bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600"
            >
              {repairing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Running…
                </>
              ) : (
                'Confirm repair'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SystemHealth;
